import { useEffect, useState } from "react";
import { useSelector, shallowEqual } from "react-redux";
import Navbar from "../common/Navbar";
import ModelViewer from "./ModelViewer";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import DicomViewer from "./DicomViewer";
import { anatomyConstants } from "../../constants/anatomyConstants";

const ThreeDModelView = () => {
  const user = useSelector((s) => s.user.currentUser);

  const modelInfos = useSelector(
    (s) => s.cases.selectedCaseDetail?.modelInfos || [],
    shallowEqual
  );
  const annotationsObj = useSelector(
    (s) => s.cases.selectedCaseDetail?.annotations || null
  );
  const measurementsObj = useSelector(
    (s) => s.cases.selectedCaseDetail?.measurements || null
  );
  const dicoms = useSelector(
    (s) => s.cases.selectedCaseDetail?.dicoms || [],
    shallowEqual
  );
  const modelGroups = useSelector(
    (s) => s.cases.selectedCaseDetail?.modelGroups || {},
    shallowEqual
  );

  const [stlURLs, setStlURLs] = useState([]);
  const [annotations, setAnnotations] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dicomFile, setDicomFile] = useState(null);
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Lock body scroll only while this page is mounted
  useEffect(() => {
    const body = document.body;
    const html = document.documentElement;
    const prevBodyOverflow = body.style.overflow;
    const prevHtmlOverflow = html.style.overflow;
    body.style.overflow = "hidden";
    html.style.overflow = "hidden";
    return () => {
      body.style.overflow = prevBodyOverflow;
      html.style.overflow = prevHtmlOverflow;
    };
  }, []);

  useEffect(() => {
    if (!user) return;
    const stlArray =
      (modelInfos || [])
        .filter((info) => info?.url && info?.name)
        .map((info) => ({
          url: info.url,
          name: info.name,
          storageName: info.storageName,
          renderOrder: info.orderNumber ?? 0,
          color: info.color ?? "#d3d3d3",
          isWatertight: info.isWatertight ?? false,
        }))
        .sort((a, b) => b.renderOrder - a.renderOrder) ?? [];
    setStlURLs(stlArray);
  }, [user, modelInfos]);

  useEffect(() => {
    if (!user) return;
    const annots = annotationsObj
      ? Object.entries(annotationsObj).map(([id, data]) => ({
          id,
          ...data,
          show: false,
        }))
      : [];
    setAnnotations(annots);
  }, [user, annotationsObj]);

  useEffect(() => {
    if (!user) return;
    const measures = measurementsObj
      ? Object.entries(measurementsObj).map(([id, data]) => ({
          id,
          ...data,
          show: false,
        }))
      : [];
    setMeasurements(measures);
  }, [user, measurementsObj]);

  useEffect(() => {
    const selectedDicom = dicoms.find((d) => d.isSelected) || dicoms[0] || null;
    if (selectedDicom) {
      setDicomFile({
        name: selectedDicom.name,
        url: selectedDicom.url,
        maskUrl: selectedDicom.maskUrl || null,
        shellUrl: selectedDicom.shellUrl || null,
        storageName: selectedDicom.storageName,
        uploadDate: selectedDicom.uploadDate,
        uploadedBy: selectedDicom.uploadedBy,
        isSelected: true,
      });
    } else {
      setDicomFile(null);
    }
    setIsFullScreen(dicoms.length === 0);
  }, [dicoms]);

  useEffect(() => {
    if (user) setLoading(false);
  }, [user]);

  const toggleFullScreen = () => setIsFullScreen((prev) => !prev);

  return (
    // Fixed layout with Navbar included at top
    <div
      className="fixed inset-0 flex flex-col w-screen overflow-hidden"
      style={{
        height: "100svh",
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
        "--main-color": anatomyConstants.mainColor,
        "--main-color-bold": anatomyConstants.mainColorBold,
        backgroundColor: "#f9fafb",
      }}
    >
      {/* ✅ Navbar renders fixed at top */}
      <Navbar />

      {/* Spacer reserves space for the fixed navbar (h-14 = 56px) */}
      <div className="shrink-0" style={{ height: "56px" }} />

      {/* Main content area fills remaining height */}
      <main className="flex-1 min-h-0 relative">
        {/* DICOM viewer (hidden when in full-screen 3D mode) */}
        <div className={isFullScreen ? "hidden" : "absolute inset-0"}>
          <DicomViewer dicomFile={dicomFile} />
        </div>

        {/* 3D viewer area */}
        <div
          className={
            isFullScreen
              ? "absolute inset-0 bg-gray-50 overflow-y-auto"
              : "absolute bottom-0 right-0 bg-gray-50 overflow-y-auto"
          }
          style={isFullScreen ? {} : { width: "50%", height: "50%" }}
        >
          {!loading && stlURLs.length > 0 && (
            <ModelViewer
              stlURLs={stlURLs}
              annotations={annotations}
              measurements={measurements}
              dicomFile={dicomFile}
              isFullScreen={isFullScreen}
              modelGroups={modelGroups}
            />
          )}

          {dicomFile && (
            <button
              className="absolute top-2 left-2 px-4 py-2 rounded text-[var(--main-color)] hover:bg-[var(--main-color)] hover:text-white"
              onClick={toggleFullScreen}
              title={isFullScreen ? "Exit full screen" : "Enter full screen"}
            >
              {isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </button>
          )}
        </div>
      </main>
    </div>
  );
};

export default ThreeDModelView;
